import { MenuItem } from "@/app/ui/navigation/types";
import { EnumAlcoholType } from "@/app/services/generated/enums/enumAlcoholType.generated";
import { LocalDate } from "@js-joda/core";
import type { Location } from "vue-router";
import type Guid from "@/app/types/common/guid";
import { dutySchemeType } from "@/components/pages/duty/composables/dutyConfiguration";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";

export const dutyMenuUk = {
  root: "navigation_duty_group_uk",
  suspendedReceipts: "duty_suspended_receipts",
  dutySuspendedOrders: "duty_suspended_orders",
  alcoholProduction: "duty_alcohol_production",
  newDutyAudit: "duty_new_duty_audit",
  viewNewAudit: (fromDate: LocalDate, toDate: LocalDate): Location => ({
    name: dutyMenuUk.newDutyAudit,
    params: {
      fromDate: fromDate.toString(),
      toDate: toDate.toString(),
    },
  }),
  newDutyReturn: "duty_new_duty_return",
  viewNewReturn: (id: Guid): Location => ({
    name: dutyMenuUk.newDutyReturn,
    params: { id: id.toString() },
  }),
  createNewReturn: (): Location => ({
    name: dutyMenuUk.newDutyReturn,
  }),
  newDutyReturnList: "duty_new_duty_return_list",
  viewNewDutyReturnList: (): Location => ({
    name: dutyMenuUk.newDutyReturnList,
  }),
  oldDutyStockList: "duty_old_duty_stock_list",
  newDutyAllOnlineSubmissionDetails:
    "duty_new_duty_all_online_submission_details",
  viewNewDutyAllOnlineSubmissionDetails: (id: Guid): Location => ({
    name: dutyMenuUk.newDutyAllOnlineSubmissionDetails,
    params: { id: id.toString() },
  }),
  newDutyBeerOnlineSubmissionDetails:
    "duty_new_duty_beer_online_submission_details",
  viewNewDutyBeerOnlineSubmissionDetails: (id: Guid): Location => ({
    name: dutyMenuUk.newDutyBeerOnlineSubmissionDetails,
    params: { id: id.toString() },
  }),
  dutyRatesCalculator: "duty_rates_calculator",
  spoiltBeerList: "spoilt_beer_list",
  legacy: {
    root: "navigation_duty_legacy_group",
    beerDuty: {
      root: "navigation_beer_duty_group",
      dashboard: "beer_duty_dashboard",
      return: "beer_duty_return",
      viewReturn: (id: Guid): Location => ({
        name: dutyMenuUk.legacy.beerDuty.return,
        params: { id: id.toString() },
      }),
      audit: "beer_duty_audit_report",
      viewAudit: (fromDate: LocalDate, toDate: LocalDate): Location => ({
        name: dutyMenuUk.legacy.beerDuty.audit,
        params: {
          fromDate: fromDate.toString(),
          toDate: toDate.toString(),
        },
      }),
      dutyReturnList: "beer_duty_return_list",
    },
    spiritDuty: {
      root: "navigation_spirit_duty_group",
      audit: "spirit_duty_audit_report",
    },
    ciderDuty: {
      root: "navigation_cider_duty_group",
      audit: "cider_duty_audit_report",
    },
    wineDuty: {
      root: "navigation_wine_duty_group",
      audit: "wine_duty_audit_report",
    },
    dutyLists: "duty_rates_list",
  },
};

export const buildDutyMenuUkItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: dutyMenuUk.root,
    icon: "Assignment",
    visible: () =>
      dutySchemeType.value.isUk() &&
      hasPermissionsUsingUserStore([EnumUserPermission.menuDutyView]),
    subMenus: [
      MenuItem.fromOpts({
        name: dutyMenuUk.newDutyReturnList,
        url: "/duty/return-list",
        view: () =>
          import(
            /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/NewDutyReturnList.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: dutyMenuUk.newDutyReturn,
        url: "/duty/return/:id?",
        view: () =>
          import(
            /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/NewDutyReturn.vue"
          ),
        componentProps: (route) => ({
          id: route.params.id,
        }),
        visible: false,
      }),
      MenuItem.fromOpts({
        name: dutyMenuUk.newDutyAudit,
        url: "/duty/audit",
        componentProps: (r) => ({
          fromDateInclusive:
            r.params.fromDate && LocalDate.parse(r.params.fromDate),
          toDateInclusive: r.params.toDate && LocalDate.parse(r.params.toDate),
        }),
        view: () =>
          import(
            /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/NewDutyAudit.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: dutyMenuUk.suspendedReceipts,
        url: "/duty/dutysuspendedreceipts/list",
        view: () =>
          import(
            /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/ListDutySuspendedReceipts.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: dutyMenuUk.dutySuspendedOrders,
        url: "/duty/dutysuspendedorders",
        view: () =>
          import(
            /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/DutySuspendedOrdersReport.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: dutyMenuUk.alcoholProduction,
        url: "/duty/alcoholproduction",
        view: () =>
          import(
            /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/AlcoholProductionFigures.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: dutyMenuUk.oldDutyStockList,
        url: "/duty/old-duty-stock-list",
        view: () =>
          import(
            /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/ListOldDutyStockPage.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: dutyMenuUk.dutyRatesCalculator,
        url: "/duty/duty-rates-calculator",
        view: () =>
          import(
            /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/ViewNewDutyRatesCalculatorPage.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: dutyMenuUk.newDutyAllOnlineSubmissionDetails,
        url: "/duty/return/all-online-submission-details/:id",
        view: () =>
          import(
            /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/NewDutyAllOnlineSubmissionDetails.vue"
          ),
        componentProps: (route) => ({
          id: route.params.id,
        }),
        visible: false,
      }),
      MenuItem.fromOpts({
        name: dutyMenuUk.newDutyBeerOnlineSubmissionDetails,
        url: "/duty/return/:id/beer-online-submission-details",
        view: () =>
          import(
            /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/NewDutyBeerOnlineSubmissionDetails.vue"
          ),
        componentProps: (route) => ({
          id: route.params.id,
        }),
        visible: false,
      }),
      MenuItem.fromOpts({
        name: dutyMenuUk.spoiltBeerList,
        url: "/duty/legacy/beer/spoiltbeer/list",
        breadcrumbParentName: dutyMenuUk.legacy.beerDuty.root,
        view: () =>
          import(
            /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/ListSpoiltBeer.vue"
          ),
      }),
      MenuItem.forBranch({
        name: dutyMenuUk.legacy.root,
        subMenus: [
          MenuItem.forBranch({
            name: dutyMenuUk.legacy.beerDuty.root,
            subMenus: [
              MenuItem.fromOpts({
                name: dutyMenuUk.legacy.beerDuty.dashboard,
                url: "/duty/legacy/beer/dashboard",
                breadcrumbParentName: dutyMenuUk.legacy.beerDuty.root,
                view: () =>
                  import(
                    /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/BeerDutyDashboard.vue"
                  ),
              }),
              MenuItem.fromOpts({
                name: dutyMenuUk.legacy.beerDuty.dutyReturnList,
                url: "/duty/legacy/beer/beerreturn/list",
                breadcrumbParentName: dutyMenuUk.legacy.beerDuty.root,
                view: () =>
                  import(
                    /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/BeerDutyReturnsList.vue"
                  ),
              }),
              MenuItem.fromOpts({
                name: dutyMenuUk.legacy.beerDuty.return,
                url: "/duty/legacy/beer/beerreturn/:id",
                breadcrumbParentName: dutyMenuUk.legacy.beerDuty.root,
                view: () =>
                  import(
                    /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/BeerDutyReturn.vue"
                  ),
                visible: false,
              }),
              MenuItem.fromOpts({
                name: dutyMenuUk.legacy.beerDuty.audit,
                url: "/duty/legacy/beer/audit",
                componentProps: (r) => ({
                  fromDateInclusive:
                    r.params.fromDate && LocalDate.parse(r.params.fromDate),
                  toDateInclusive:
                    r.params.toDate && LocalDate.parse(r.params.toDate),
                  alcoholType: [EnumAlcoholType.beer],
                }),
                view: () =>
                  import(
                    /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/DutyAudit.vue"
                  ),
              }),
            ],
          }),
          MenuItem.forBranch({
            name: dutyMenuUk.legacy.spiritDuty.root,
            subMenus: [
              MenuItem.fromOpts({
                name: dutyMenuUk.legacy.spiritDuty.audit,
                url: "/duty/legacy/spirit/audit",
                breadcrumbParentName: dutyMenuUk.legacy.spiritDuty.root,
                componentProps: {
                  alcoholType: [EnumAlcoholType.spirits],
                },
                view: () =>
                  import(
                    /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/DutyAudit.vue"
                  ),
              }),
            ],
          }),
          MenuItem.forBranch({
            name: dutyMenuUk.legacy.ciderDuty.root,
            subMenus: [
              MenuItem.fromOpts({
                name: dutyMenuUk.legacy.ciderDuty.audit,
                url: "/duty/legacy/cider/audit",
                breadcrumbParentName: dutyMenuUk.legacy.ciderDuty.root,
                componentProps: {
                  alcoholType: [
                    EnumAlcoholType.stillCiderPerry,
                    EnumAlcoholType.sparklingCiderPerry,
                  ],
                },

                view: () =>
                  import(
                    /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/DutyAudit.vue"
                  ),
              }),
            ],
          }),
          MenuItem.forBranch({
            name: dutyMenuUk.legacy.wineDuty.root,
            subMenus: [
              MenuItem.fromOpts({
                name: dutyMenuUk.legacy.wineDuty.audit,
                breadcrumbParentName: dutyMenuUk.legacy.wineDuty.root,
                url: "/duty/legacy/wine/audit",
                componentProps: {
                  alcoholType: [
                    EnumAlcoholType.stillWine,
                    EnumAlcoholType.sparklingWine,
                  ],
                },

                view: () =>
                  import(
                    /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/DutyAudit.vue"
                  ),
              }),
            ],
          }),
          MenuItem.fromOpts({
            name: dutyMenuUk.legacy.dutyLists,
            url: "/duty/legacy/list",
            view: () =>
              import(
                /* webpackChunkName: "duty" */ "@/components/pages/duty/uk/DutyRatesList.vue"
              ),
          }),
        ],
      }),
    ],
  }),
];
